import { observer } from 'mobx-react'
import { useEffect, useRef } from 'react'
import { StyleSheet, View, ViewProps } from 'react-native'
import { DataWithUpload, Draft, FloorplanData, WorkspaceData, FloorplanWorkspaceData, useFloorplan } from 'zens-store'
import { Color, Radius, Spacing } from 'zens-storybook'

import { NavigationConfirm } from '../navigation'

import FloorplanSettings from './settings'
import FloorplanPlacement from './placement'

const styles = StyleSheet.create({
  container: {},
  content: {
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
    margin: Spacing.L,
  },
})

export enum FloorplanEditorStep {
  Add,
  Edit,
  Settings,
}

interface Props extends ViewProps {
  companyId: number
  buildingId: number
  floorId?: number
  step: FloorplanEditorStep
  onSaveDraft?: (draft: Draft<DataWithUpload<FloorplanData>>, shouldClose?: boolean) => Promise<void>
  onSaveWorkspace?: (draft: Draft<WorkspaceData>) => Promise<void>
  onRemoveWorkspace?: (draft: Draft<WorkspaceData>) => Promise<void>
  onDuplicateWorkspaces?: (
    workspaces: FloorplanWorkspaceData[],
    draft: Draft<FloorplanData>,
  ) => Promise<FloorplanWorkspaceData[] | void>
  onCancel?: (draft: Draft<FloorplanData>) => Promise<void>
  onDelete?: (draft: Draft<FloorplanData>) => Promise<void>
  onPublish?: (draft: Draft<FloorplanData>) => Promise<void>
  onDiscard?: (draft: Draft<FloorplanData>) => Promise<void>
  onSettings?: () => void
  errors?: Record<string, string>
}

const FloorplanEditor = (props: Props) => {
  const {
    companyId,
    buildingId,
    floorId = 0,
    step,
    errors,
    onSaveDraft,
    onSaveWorkspace,
    onRemoveWorkspace,
    onDuplicateWorkspaces,
    onCancel,
    onDelete,
    onPublish,
    onDiscard,
    onSettings,
    style,
    ...otherProps
  } = props
  const floorplan = useFloorplan({ companyId, floorId })
  const draft = useRef(
    new Draft<DataWithUpload<FloorplanData>>({
      id: NaN,
      company_id: companyId,
      building_id: buildingId,
      ...floorplan.data?.data,
    }),
  ).current

  useEffect(() => {
    if (!floorplan.data?.data) return
    ;(Object.keys(floorplan.data?.data) as (keyof FloorplanData)[]).forEach((key) => {
      draft.update(key, floorplan.data?.data[key])
    })
  }, [!!floorplan.isFetchedAfterMount])

  async function saveWorkspace(value: Draft<WorkspaceData>) {
    await onSaveWorkspace?.(value)
    const newWorkspaces = [...(draft.data.workspaces || [])]
    if (value.changedData.id) {
      newWorkspaces.push({ ...value.data })
    } else {
      const index = newWorkspaces.findIndex((item) => item.id === value.data.id)
      newWorkspaces[index] = { ...value.data }
    }
    draft.update('workspaces', newWorkspaces)
  }

  async function duplicateWorkspaces(workspaces: FloorplanWorkspaceData[]) {
    return onDuplicateWorkspaces?.(workspaces, draft)
  }

  async function saveDraft() {
    if (!draft.hasChanges) return onCancel?.(draft)
    else return onSaveDraft?.(draft, true)
  }

  async function saveSettings() {
    return onSaveDraft?.(draft, false)
  }

  async function cancel() {
    return onCancel?.(draft)
  }

  async function publish() {
    return onPublish?.(draft)
  }

  async function discard() {
    return onDiscard?.(draft)
  }

  async function remove() {
    return onDelete?.(draft)
  }

  function renderContent() {
    switch (step) {
      case FloorplanEditorStep.Add:
      case FloorplanEditorStep.Edit: {
        if (
          !floorplan.data?.background ||
          !floorplan.data?.scale?.distance ||
          !floorplan.data?.scale?.start ||
          !floorplan.data?.scale?.end
        )
          return (
            <FloorplanSettings
              style={styles.content}
              draft={draft}
              onSave={saveSettings}
              onCancel={cancel}
              onDelete={remove}
              errors={errors}
            />
          )
        return (
          <FloorplanPlacement
            style={styles.content}
            draft={draft}
            onSaveDraft={saveDraft}
            onSaveWorkspace={saveWorkspace}
            onRemoveWorkspace={onRemoveWorkspace}
            onDuplicateWorkspaces={duplicateWorkspaces}
            onPublish={publish}
            onDiscard={discard}
            onSettings={onSettings}
            errors={errors}
          />
        )
      }
      case FloorplanEditorStep.Settings:
        return (
          <FloorplanSettings
            style={styles.content}
            draft={draft}
            onSave={saveSettings}
            onCancel={cancel}
            onDelete={remove}
            errors={errors}
          />
        )
    }
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      {renderContent()}
      <NavigationConfirm draft={draft} />
    </View>
  )
}

interface SkeletonProps extends ViewProps {
  step: FloorplanEditorStep
}
const Skeleton = (props: SkeletonProps) => {
  const { step, style, ...otherProps } = props
  function renderContent() {
    switch (step) {
      case FloorplanEditorStep.Edit: {
        return <FloorplanPlacement.Skeleton style={styles.content} />
      }
      case FloorplanEditorStep.Add:
      case FloorplanEditorStep.Settings:
        return <FloorplanSettings.Skeleton style={styles.content} />
    }
  }
  return (
    <View style={[styles.container, style]} {...otherProps}>
      {renderContent()}
    </View>
  )
}
FloorplanEditor.Skeleton = Skeleton

export default observer(FloorplanEditor)
